// Removed Month Start from filter and added standalone picker

import { pipe, map, flatten, filter } from 'ramda';
import React, { useState, useMemo, useEffect } from 'react';
import { RevenueForecastStyled } from './revenue-forecast.style';
import { Switch, Typography } from '@mui/material';
import FilterComponent from '../../components/filter/filter';
import BarChart from '../../components/charts/bar-chart/bar-chart';
import ForecastedProjects from './forecasted-projects/forecasted-projects';
import dayjs from 'dayjs';
import { useGetForecastData } from '../../components/filter/filter.utils';
import {
  normalizeDate,
  alignScheduleToStartMonth,
  parseScheduleValue,
  calculateFilterRange,
  isProjectScheduleWithinRange,
} from './revenue-forecast.utils';
import MonthYearPickerField from '../../components/form-fields/month-year-picker/MonthYearPickerField';
import MonthPickerField from '../../components/form-fields/month-year-picker/MonthPickerField';
import ForecastedClients from './forecasted-clients/forecasted-clients';
import { isSomething } from '../../utils/utils';
import Loader from '../../components/loader/loader';
import TableActionMenu from '../../components/table-action-menu/table-action-menu';
import useCompanies from '../../hooks/useCompanies';
import { downloadForecastCSV } from './csv/revenue-forecast.csv';

const RevenueForecast = () => {
  const [view, setView] = useState('project'); // 'project' or 'client'
  const currentMonth = new Date().toLocaleString('default', {
    month: 'long',
    year: 'numeric',
  });
  const [filterState, setFilterState] = useState([]);

  const [monthStart, setMonthStart] = useState(currentMonth);
  const [selectedMonths, setSelectedMonths] = useState(6); // Default value
  const { userFilteredProjects, options, isLoading, clientsList } = useGetForecastData();
  const { companies } = useCompanies();
  const companiesMap = useMemo(() => {
    if (!companies) return {};
    return companies.reduce((acc, company) => {
      acc[company.companyID] = company.companyName;
      return acc;
    }, {});
  }, [companies]);

  useEffect(() => {
    if (isLoading) return;
    const ops = pipe(
      filter((opt) => opt.key !== 'probability'),
      map((opt) => {
        const color = opt.color;
        const key = opt.key;
        const titleName = opt.name;

        const internalOptions = opt.options.map((o) => ({
          value: o.value,
          color,
          key,
          titleName,
        }));
        return internalOptions;
      }),
      flatten,
    )(options);
    setFilterState((prev) => [
      ...prev,
      ...ops,
      ...[
        {
          key: 'probability',
          value: '80',
          color: '#E4F9DC',
          titleName: 'Probability',
        },
        {
          key: 'probability',
          value: '100',
          color: '#E4F9DC',
          titleName: 'Probability',
        },
      ],
    ]);
  }, [options]);
  // Apply filters to projects
  const filteredProjects = useMemo(() => {
    const excludeStates = ['On Hold', 'Cancelled'];

    const groupedFilters = filterState.reduce((acc, { key, value }) => {
      if (!acc[key]) acc[key] = [];
      acc[key].push(value);
      return acc;
    }, {});

    const selectedProbabilities = groupedFilters.probability
      ? groupedFilters.probability.map((v) => parseInt(v, 10))
      : [];

    return userFilteredProjects.filter((project) => {
      // Exclude On Hold and Cancelled states by default
      if (excludeStates.includes(project.state)) return false;

      // STATE filter logic (if state filter is explicitly selected)
      if (
        groupedFilters.state &&
        groupedFilters.state.length > 0 &&
        !groupedFilters.state.includes(project.state)
      ) {
        return false;
      }

      // PROBABILITY filter logic applies ONLY if project is "Opportunity"
      if (
        ['Opportunity'].includes(project.state) &&
        selectedProbabilities.length > 0 &&
        !selectedProbabilities.some((prob) => parseInt(project.probability, 10) == prob)
      ) {
        return false;
      }

      // CLIENT filter logic (no "all" option)
      if (
        groupedFilters.client &&
        groupedFilters.client.length > 0 &&
        !groupedFilters.client.includes(project.client)
      ) {
        return false;
      }

      // GROUP filter logic (uncomment if needed, currently commented out)
      // if (
      //   groupedFilters.group &&
      //   groupedFilters.group.length > 0 &&
      //   !groupedFilters.group.includes(project.addToExistingProject)
      // ) {
      //   return false;
      // }

      return true;
    });
  }, [userFilteredProjects, filterState]);

  const filtersRange = calculateFilterRange(monthStart, parseInt(selectedMonths || 6));

  const formattedProjects = useMemo(() => {
    return filteredProjects
      .filter((x) => {
        if (!x?.schedule || !x?.start) return false;
        const isWithinRange = isProjectScheduleWithinRange(
          x?.schedule,
          normalizeDate(x?.start),
          filtersRange,
        );

        // Exclude projects with 0 values in all months
        const scheduleRange = alignScheduleToStartMonth(
          new Date(normalizeDate(x?.start)),
          x?.schedule,
        );
        const hasNonZeroValues = scheduleRange.some(
          (item) =>
            filtersRange.includes(dayjs(item.date).format('MMM-YYYY')) &&
            parseScheduleValue(item.value) > 0,
        );

        return isWithinRange && hasNonZeroValues;
      })
      .map((x) => {
        const projectStartDate = new Date(normalizeDate(x?.start));
        const scheduleRange = alignScheduleToStartMonth(projectStartDate, x?.schedule).map((x) => ({
          date: dayjs(x?.date).format('MMM-YYYY'),
          value: parseScheduleValue(x.value),
        }));

        return { ...x, formattedSchedule: scheduleRange };
      });
  }, [filteredProjects, filtersRange]);

  const chartData = useMemo(() => {
    const data = {};
    formattedProjects.forEach((project) => {
      project.formattedSchedule.forEach((scheduleItem) => {
        if (filtersRange.includes(scheduleItem.date)) {
          if (!data[scheduleItem.date]) {
            data[scheduleItem.date] = {
              name: scheduleItem.date,
              activeValue: 0,
              opportunityValue: 0,
              completeValue: 0,
              committedValue: 0,
            };
          }
          const value = parseScheduleValue(scheduleItem.value);
          if (project.state === 'Active') {
            data[scheduleItem.date].activeValue += value;
          } else if ('Opportunity' === project.state) {
            data[scheduleItem.date].opportunityValue += value;
          } else if ('Committed' === project.state) {
            data[scheduleItem.date].committedValue += value;
          } else if (project.state === 'Complete') {
            data[scheduleItem.date].completeValue += value;
          }
        }
      });
    });
    return Object.values(data).sort((a, b) => new Date(a.name) - new Date(b.name));
  }, [formattedProjects, filtersRange]);

  const handleDownloadCSV = () => {
    downloadForecastCSV({ view, formattedProjects, filtersRange, companiesMap });
  };

  if (isLoading) {
    return (
      <div>
        <Loader absolute />
      </div>
    );
  }
  return (
    <RevenueForecastStyled>
      <div className="header">
        <Typography variant="h4">Revenue Forecast</Typography>
      </div>
      <div className="content-box">
        <div className="parent-div">
          <div className="content-head">
            <div className="forecastStart">
              <Typography variant="subtitle1" className="forecast-date">
                Forecast Start Date:
              </Typography>

              <MonthYearPickerField
                value={monthStart}
                onChange={(date) => setMonthStart(date.format('MMMM YYYY'))}
                maxYear="2028"
              />

              <MonthPickerField
                value={selectedMonths}
                onChange={(value) => setSelectedMonths(value)}
                placeholder="Select Duration"
                options={[
                  { label: '1 Month', value: 1 },
                  { label: '3 Months', value: 3 },
                  { label: '6 Months', value: 6 },
                  { label: '9 Months', value: 9 },
                  { label: '12 Months', value: 12 },
                  { label: '18 Months', value: 18 },
                ]}
              />
            </div>
            <div className="filterwrapper">
              {isSomething(filterState) && (
                <FilterComponent
                  label="Filter Forecast"
                  options={options.filter((opt) => opt.key !== 'start')}
                  state={filterState}
                  setState={setFilterState}
                  isLoading={isLoading}
                  onClearFilters={() => setFilterState([])}
                  disablePaddingBorder={true}
                  className="class-check"
                />
              )}
            </div>
          </div>
        </div>
        <div className="content">
          <BarChart key={JSON.stringify(chartData)} data={chartData} />

          <div className="list">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" sx={{ marginBottom: '10px' }}>
                Forecasted Projects
              </Typography>

              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography variant="subtitle2" sx={{ display: 'inline', marginRight: '10px' }}>
                  Client View
                </Typography>
                <Switch
                  value={view === 'client'}
                  onChange={(e) => {
                    setView(e.target.checked ? 'client' : 'project');
                  }}
                />
                <TableActionMenu
                  options={[
                    {
                      title: 'Download CSV',
                      onClick: handleDownloadCSV,
                    },
                  ]}
                  width={160}
                  noBorder
                />
              </div>
            </div>
            {view === 'project' ? (
              <ForecastedProjects projects={formattedProjects} filtersRange={filtersRange} />
            ) : (
              <ForecastedClients
                projects={formattedProjects}
                clientsList={clientsList}
                filtersRange={filtersRange}
              />
            )}
          </div>
        </div>
      </div>
    </RevenueForecastStyled>
  );
};

export default RevenueForecast;
